import { useState, useEffect, useCallback, useMemo } from 'react';
import { Select, SelectItem } from '@nextui-org/react';
import Header from '../../../components/layouts/Header';
import ModalSlideFilter from '../../../../../common/src/components/filters/modal-sidebar-filter';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import useFilterStore from '../../../../../common/src/stores/useFiltersStore';
import useSharingStore from '../../../store/useSharingStore';
import { getCostsVsExporters } from 'common/src/api/settlements';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import CostDispertionChart from '../../../components/settlements/comparative-costs/CostDispertionChart';
import UnitPriceFilter from 'common/src/components/filters/UnitPriceFilter';
import CountryFilter from '../../../../../common/src/components/filters/CountryFilter';
import CostsVarietyFilter from '../../../../../common/src/components/filters/CostsVarietyFilter';
import DispertionChartSkeleton from '../../../components/settlements/comparative-sales/DispertionChartSkeleton';
import SeasonFilter from '../../../../../common/src/components/filters/SeasonFilter';
import CostFilterType from '../../../../../common/src/components/filters/CostFilterType';
import { getSeasonWeeksForSpecies } from 'common/src/utils/seasonWeeks';
import { Alert, FiltersToggle, PageLayout } from '../../../components/ui';

const SettlementsComparativeCosts = () => {
  const { t } = useTranslation('settlementsComparative');
  const { session, userProfile } = useUserStore();
  const { selectedSpecies } = useDataStore();
  const { selectedSeason } = useFilterStore();
  const { fetchExceptions, isRestricted, exceptions } = useSharingStore();

  // Access control state
  const [hasAccess, setHasAccess] = useState(true);

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [countriesLoaded, setCountriesLoaded] = useState(false);
  const [initialCountrySet, setInitialCountrySet] = useState(false);
  const [varietiesLoaded, setVarietiesLoaded] = useState(false);
  const [initialVarietySet, setInitialVarietySet] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);
  const weeks = useMemo(
    () => getSeasonWeeksForSpecies(selectedSpecies?.id, selectedSeason),
    [selectedSpecies?.id, selectedSeason]
  );
  const [filters, setFilters] = useState({
    methodology: 'by_volume',
    separation: 'none',
    shipmentType: 'M', // Add shipment type filter
    unitPrice: 'kg', // Default to kg
    country: 'all', // Add country filter
    variety: 'all', // Add variety filter
    costFilter: 'all', // Default to show all settlements
  });
  const [visualization, setVisualization] = useState<'chart' | 'table'>(
    'chart'
  );

  const [costsData, setCostsData] = useState<{
    [key: string]: {
      own: any[];
      others: {
        min: number;
        q1: number;
        median: number;
        q3: number;
        max: number;
      };
      ownKg: number;
      othersKg: number;
    };
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isDemo = !!userProfile?.exporter?.hasDemo;

  // Combined filters loading state
  const filtersLoading =
    !countriesLoaded ||
    !varietiesLoaded ||
    !initialCountrySet ||
    !initialVarietySet;

  // Debug logs
  console.log('🔍 Loading states:', {
    countriesLoaded,
    varietiesLoaded,
    initialCountrySet,
    initialVarietySet,
    filtersLoading,
  });

  // Fallback: if filters take too long, set them as loaded after 10 seconds
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!countriesLoaded || !varietiesLoaded) {
        console.log('⚠️ Filters timeout - forcing load completion');
        setCountriesLoaded(true);
        setVarietiesLoaded(true);
        setInitialCountrySet(true);
        setInitialVarietySet(true);
      }
    }, 10000); // 10 seconds timeout

    return () => clearTimeout(timeout);
  }, [countriesLoaded, varietiesLoaded]);

  // Force load completion for non-cherry species
  useEffect(() => {
    if (selectedSpecies && selectedSpecies.id !== 2) {
      console.log('🍍 Non-cherry species detected, forcing filter completion');
      setCountriesLoaded(true);
      setVarietiesLoaded(true);
      setInitialCountrySet(true);
      setInitialVarietySet(true);
    }
  }, [selectedSpecies]);

  // Fetch sharing exceptions when user profile is available
  useEffect(() => {
    if (userProfile?.exporter_id) {
      fetchExceptions(userProfile.exporter_id);
    }
  }, [userProfile?.exporter_id, fetchExceptions]);

  // Check if access is restricted for current species/season
  useEffect(() => {
    if (!selectedSpecies || !userProfile || !selectedSeason) {
      console.log('🔓 Costs: Missing data for access check', {
        selectedSpecies,
        userProfile,
        selectedSeason,
      });
      setHasAccess(true);
      return;
    }

    const restricted = isRestricted(selectedSpecies.id, selectedSeason);
    console.log('🔐 Costs: Access check result', {
      speciesId: selectedSpecies.id,
      season: selectedSeason,
      restricted,
      hasAccess: !restricted,
    });
    setHasAccess(!restricted);
  }, [selectedSpecies, selectedSeason, userProfile, isRestricted, exceptions]);

  // Filter options
  const filterOptions = {
    methodology: [
      { key: 'by_volume', label: t('Por Volumen') },
      { key: 'by_exporter', label: t('Por Exportadora') },
    ],
    // Removed groupBy filter from UI
    separation: [
      { key: 'none', label: t('Sin Separación') },
      { key: 'by_receiver', label: t('Por Cliente') },
    ],
    shipmentType: [
      { key: 'both', label: t('Todos') },
      { key: 'A', label: t('Solo Aéreos') },
      { key: 'M', label: t('Solo Marítimos') },
      { key: 'T', label: t('Solo Terrestres') },
    ],
  };

  const handleFilterChange = (filterName: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const handleCountriesLoaded = useCallback(
    (mostCommon: string | null) => {
      console.log('🌍 Countries loaded callback:', {
        mostCommon,
        initialCountrySet,
      });
      setCountriesLoaded(true);
      if (mostCommon && !initialCountrySet) {
        setFilters((prev) => ({ ...prev, country: mostCommon }));
        setInitialCountrySet(true);
        console.log('🌍 Auto-selected most common country:', mostCommon);
      } else if (!mostCommon) {
        // Even if no mostCommon, mark as set to avoid infinite loading
        setInitialCountrySet(true);
        console.log('🌍 No countries found, but marking as set');
      }
    },
    [initialCountrySet]
  );

  const handleVarietiesLoaded = useCallback(
    (mostCommon: string | null) => {
      console.log('🍇 Varieties loaded callback:', {
        mostCommon,
        initialVarietySet,
      });
      setVarietiesLoaded(true);
      if (mostCommon && !initialVarietySet) {
        setFilters((prev) => ({ ...prev, variety: mostCommon }));
        setInitialVarietySet(true);
        console.log('🍇 Auto-selected most common variety:', mostCommon);
      } else if (!mostCommon) {
        // Even if no mostCommon, mark as set to avoid infinite loading
        setInitialVarietySet(true);
        console.log('🍇 No varieties found, but marking as set');
      }
    },
    [initialVarietySet]
  );

  // Fetch costs data
  const fetchCostsData = async () => {
    if (
      !session ||
      !userProfile?.exporter_id ||
      !selectedSeason ||
      !selectedSpecies ||
      selectedSpecies.id !== 2 ||
      !countriesLoaded || // Wait for countries to be loaded
      !initialCountrySet || // Wait for initial country to be set
      !varietiesLoaded || // Wait for varieties to be loaded
      !initialVarietySet // Wait for initial variety to be set
    ) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('🔍 Fetching costs data with params:', {
        methodology: filters.methodology,
        selectedSeason,
        speciesId: selectedSpecies.id,
        separation: filters.separation,
        shipmentType: filters.shipmentType,
      });

      const data = await getCostsVsExporters(
        session,
        filters.methodology,
        selectedSeason,
        selectedSpecies.id,
        filters.separation,
        'all', // Use all markets
        filters.shipmentType, // Add shipment type parameter
        filters.country, // Add country filter parameter
        filters.variety, // Add variety filter parameter
        selectedWeek || undefined, // Add week filter parameter
        filters.costFilter // Add cost filter parameter
      );

      console.log('📊 Received costs data:', data);

      // The data structure is now consolidated, so we store it directly
      setCostsData(data);
    } catch (err) {
      console.error('Error fetching costs data:', err);
      setError(err instanceof Error ? err.message : t('Error al cargar datos'));
    } finally {
      setLoading(false);
    }
  };

  // Process data for chart - Updated for consolidated data
  const processDataForChart = (data: any, costType: string) => {
    console.log(`🔄 Processing data for ${costType}:`, data);

    if (!data || !data[costType]) {
      console.log(`❌ No data found for ${costType}`);
      return null;
    }

    const costData = data[costType];
    console.log(`📈 Cost data for ${costType}:`, costData);

    // Check if we need to multiply by default_unit_price (for fixed costs when not kg)
    const isFixedCost = costType === 'freight' || costType === 'other_costs';
    const isBoxUnit = filters.unitPrice.startsWith('box-');
    const multiplier =
      isFixedCost && isBoxUnit && selectedSpecies?.default_unit_price
        ? selectedSpecies.default_unit_price
        : 1;

    const others: any[] = [];
    const own: any[] = [];

    // Process others data (boxplot) - single consolidated boxplot
    if (costData.others && costData.othersKg > 0) {
      const othersStats = costData.others;
      console.log(`📦 Others stats for ${costType}:`, othersStats);

      // Check if we have valid statistics
      if (othersStats.min !== undefined && othersStats.max !== undefined) {
        // Apply multiplier for fixed costs when using box units
        const adjusted = [
          othersStats.min * multiplier,
          othersStats.q1 * multiplier,
          othersStats.median * multiplier,
          othersStats.q3 * multiplier,
          othersStats.max * multiplier,
        ].sort((a, b) => a - b);

        // Add tiny epsilon if degenerate to keep box visible
        const min = adjusted[0];
        const median = adjusted[2];
        const max = adjusted[4];
        const eps = 0.01;
        const finalAdjusted =
          min === max
            ? [
                median - 2 * eps,
                median - eps,
                median,
                median + eps,
                median + 2 * eps,
              ]
            : adjusted;
        others.push({
          x: getTitleForCostType(costType),
          y: finalAdjusted as any,
        });
        console.log(
          `✅ Added others data for ${costType} (multiplier: ${multiplier}):`,
          others[0]
        );
      }
    }

    // Process own data (scatter points) - based on separation
    if (costData.own && costData.own.length > 0) {
      console.log(`👤 Own data for ${costType}:`, costData.own);

      costData.own.forEach((ownPoint: any) => {
        let label = getTitleForCostType(costType);

        // Add separation info to the label if exists
        if (ownPoint.receiver) {
          label = ownPoint.receiver;
        }

        own.push({
          x: getTitleForCostType(costType),
          y: ownPoint.value * multiplier, // Apply multiplier
          label: label, // For tooltip/legend
          kilos: ownPoint.kilos,
        });
      });
      console.log(
        `✅ Added own data for ${costType} (multiplier: ${multiplier}):`,
        own
      );
    }

    const result = { others, own };
    console.log(`🎯 Final processed data for ${costType}:`, result);
    return result;
  };

  // Helper function to get display title for cost type
  const getTitleForCostType = (costType: string): string => {
    const isBoxUnit = filters.unitPrice.startsWith('box-');
    const boxSize = isBoxUnit ? filters.unitPrice.split('-')[1] : null;

    switch (costType) {
      case 'commission':
        return t('Comisión (%)');
      case 'tax':
        return t('Impuestos (%)');
      case 'freight':
        return isBoxUnit
          ? `${t('Flete')} (USD/${t('caja')} ${boxSize}kg)`
          : `${t('Flete')} (USD/kg)`;
      case 'other_costs':
        return isBoxUnit
          ? `${t('Otros Costos')} (USD/${t('caja')} ${boxSize}kg)`
          : `${t('Otros Costos')} (USD/kg)`;
      default:
        return costType;
    }
  };

  // Effects
  useEffect(() => {
    fetchCostsData();
  }, [
    selectedSeason,
    selectedSpecies,
    filters.methodology,
    filters.separation,
    filters.shipmentType,
    filters.country, // Add country filter to dependencies
    countriesLoaded, // Add countries loaded state
    initialCountrySet, // Add initial country set state
    filters.variety, // Add variety filter to dependencies
    varietiesLoaded, // Add varieties loaded state
    initialVarietySet, // Add initial variety set state
    selectedWeek, // Add week filter to dependencies
    filters.costFilter, // Add cost filter to dependencies
    // unitPrice is NOT included - we only convert in frontend, no backend call needed
  ]);

  // Clear error when switching to cherry species
  useEffect(() => {
    if (selectedSpecies?.id === 2) {
      setError(null);
    }
  }, [selectedSpecies]);

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      <ModalSlideFilter
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
      >
        <div className='bg-background p-4 pt-8 h-full overflow-y-auto'>
          <div className='space-y-4'>
            {/* Error message display */}
            {error && (
              <Alert variant='danger' className='mb-4'>
                {error}
              </Alert>
            )}

            <p className='text-sm text-default-500 mb-1'>
              {t('Filtros y Agrupación')}
            </p>

            <div className={isDemo ? 'pointer-events-none' : ''}>
              <SeasonFilter
                className='w-full'
                size='md'
                label={t('Temporada')}
              />
            </div>

            <CostFilterType
              value={filters.costFilter}
              onChange={(value) => handleFilterChange('costFilter', value)}
              className='w-full'
              disabled={selectedSpecies?.id !== 2 || loading || !hasAccess}
              label={t('Metodología Costos')}
            />

            <Select
              label={t('Semana')}
              selectedKeys={selectedWeek ? [selectedWeek.toString()] : ['all']}
              onSelectionChange={(keys) => {
                const value = Array.from(keys)[0] as string;
                setSelectedWeek(value === 'all' ? null : parseInt(value));
              }}
              className='w-full'
              isDisabled={
                loading ||
                userProfile?.exporter?.hasDemo ||
                selectedSpecies?.id !== 2
              }
              description={
                userProfile?.exporter?.hasDemo
                  ? t('Contrata CherryMarket para ver todas las semanas')
                  : ''
              }
              items={[
                { key: 'all', label: t('Todas las semanas') },
                ...weeks.map((week) => ({
                  key: week.toString(),
                  label: `${t('Semana')} ${week}`,
                })),
              ]}
            >
              {(item) => <SelectItem key={item.key}>{item.label}</SelectItem>}
            </Select>

            <Select
              label={t('Ponderación')}
              value={filters.methodology}
              onChange={(e) =>
                handleFilterChange('methodology', e.target.value)
              }
              defaultSelectedKeys={[filters.methodology]}
              className='w-full'
              isDisabled={isDemo}
            >
              {filterOptions.methodology.map((option) => (
                <SelectItem key={option.key} value={option.key}>
                  {option.label}
                </SelectItem>
              ))}
            </Select>

            {/* Agrupación removed */}

            <Select
              label={t('Separación')}
              value={filters.separation}
              onChange={(e) => handleFilterChange('separation', e.target.value)}
              defaultSelectedKeys={[filters.separation]}
              className='w-full'
              isDisabled={isDemo}
            >
              {filterOptions.separation.map((option) => (
                <SelectItem key={option.key} value={option.key}>
                  {option.label}
                </SelectItem>
              ))}
            </Select>

            <Select
              label={t('Tipo de Flete')}
              value={filters.shipmentType}
              onChange={(e) =>
                handleFilterChange('shipmentType', e.target.value)
              }
              defaultSelectedKeys={[filters.shipmentType]}
              className='w-full'
              isDisabled={isDemo}
            >
              {filterOptions.shipmentType.map((option) => (
                <SelectItem key={option.key} value={option.key}>
                  {option.label}
                </SelectItem>
              ))}
            </Select>

            <div className={isDemo ? 'pointer-events-none' : ''}>
              <UnitPriceFilter
                selectedSpecies={selectedSpecies}
                value={filters.unitPrice}
                onChange={(value) => handleFilterChange('unitPrice', value)}
                className='w-full'
                label={`Kg o Caja Eq.${
                  selectedSpecies?.default_unit_price
                    ? ` (${selectedSpecies.default_unit_price}kg)`
                    : ''
                }`}
              />
            </div>

            <CountryFilter
              value={filters.country}
              onChange={(value) => handleFilterChange('country', value)}
              onCountriesLoaded={handleCountriesLoaded}
              className='w-full'
              disabled={
                isDemo || selectedSpecies?.id !== 2 || loading || !hasAccess
              }
            />

            <CostsVarietyFilter
              value={filters.variety}
              onChange={(value) => handleFilterChange('variety', value)}
              onVarietiesLoaded={handleVarietiesLoaded}
              className='w-full'
              disabled={
                isDemo || selectedSpecies?.id !== 2 || loading || !hasAccess
              }
            />

            <p className='text-sm text-default-500 mb-1'>
              {t('Visualización')}
            </p>
            <Select
              label={t('Tipo de visualización')}
              value={visualization}
              onChange={(e) =>
                setVisualization(e.target.value as 'chart' | 'table')
              }
              defaultSelectedKeys={[visualization]}
              className='w-full'
              isDisabled={isDemo}
            >
              <SelectItem key='chart' value='chart'>
                {t('Gráficos')}
              </SelectItem>
              <SelectItem key='table' value='table'>
                {t('Tablas')}
              </SelectItem>
            </Select>
          </div>
        </div>
      </ModalSlideFilter>

      <PageLayout>
        <Header
          title={t('Comparativo de Costos Industria')}
          description={t('Análisis comparativo de costos entre exportadoras')}
        />

        {isDemo && (
          <Alert className='mb-4'>
            {t(
              'Estás viendo una cuenta demo. Los filtros están bloqueados y la data mostrada es referencial/ficticia.'
            )}
          </Alert>
        )}

        <div className='mb-4'>
          <FiltersToggle
            isOpen={isFilterOpen}
            onToggle={() => setIsFilterOpen(!isFilterOpen)}
            label={isFilterOpen ? t('Ocultar filtros') : t('Mostrar filtros')}
          />
        </div>

        {selectedSpecies?.id !== 2 && (
          <Alert variant='danger' className='mb-4'>
            {t(
              'Esta visualización solo está disponible para cerezas. Por favor selecciona cereza en el filtro de especies.'
            )}
          </Alert>
        )}

        {!hasAccess && selectedSpecies?.id === 2 && (
          <Alert variant='warning' className='mb-4'>
            {t(
              'Tu organización no tiene permisos para acceder al módulo Industria para esta especie/temporada.'
            )}
          </Alert>
        )}

        {selectedSpecies?.id === 2 && (
          <div
            className={`grid gap-6 mt-6 ${!hasAccess ? 'blur-md pointer-events-none select-none' : ''}`}
          >
            {/* Show skeletons while filters are loading */}
            {filtersLoading ? (
              <>
                {/* Costos Variables Skeleton */}
                <div>
                  <h3 className='text-lg font-semibold mb-4'>
                    {t('Costos Variables (%)')}
                  </h3>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                    <DispertionChartSkeleton />
                    <DispertionChartSkeleton />
                  </div>
                </div>

                {/* Costos Fijos Skeleton */}
                <div>
                  <h3 className='text-lg font-semibold mb-4'>
                    {t('Costos Fijos (USD/kg)')}
                  </h3>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                    <DispertionChartSkeleton />
                    <DispertionChartSkeleton />
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Costos Variables (%) */}
                <div>
                  <h3 className='text-lg font-semibold mb-4'>
                    {t('Costos Variables (%)')}
                  </h3>
                  <div className='relative'>
                    <div
                      className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${
                        isDemo ? 'blur-sm select-none pointer-events-none' : ''
                      }`}
                    >
                      <CostDispertionChart
                        data={processDataForChart(costsData, 'commission')}
                        title='Comisión (%)'
                        unit='%'
                        separation={
                          filters.separation as 'none' | 'by_receiver'
                        }
                        loading={loading}
                        visualization={visualization}
                        othersKg={costsData?.commission?.othersKg || 0}
                        ownKg={costsData?.commission?.ownKg || 0}
                      />
                      <CostDispertionChart
                        data={processDataForChart(costsData, 'tax')}
                        title='Impuestos (%)'
                        unit='%'
                        separation={
                          filters.separation as 'none' | 'by_receiver'
                        }
                        loading={loading}
                        visualization={visualization}
                        othersKg={costsData?.tax?.othersKg || 0}
                        ownKg={costsData?.tax?.ownKg || 0}
                      />
                    </div>
                    {isDemo && (
                      <div className='absolute inset-0 z-10 flex items-center justify-center'>
                        <Alert className='text-center shadow'>
                          {t(
                            'Estadísticas ocultas en modo demo. Contrata CherryMarket para ver los valores reales.'
                          )}
                        </Alert>
                      </div>
                    )}
                  </div>
                </div>

                {/* Costos Fijos (USD/unit) */}
                <div>
                  <h3 className='text-lg font-semibold mb-4'>
                    {t('Costos Fijos')} (
                    {filters.unitPrice.startsWith('box-')
                      ? `USD/${t('caja')} ${filters.unitPrice.split('-')[1]}kg`
                      : 'USD/kg'}
                    )
                  </h3>
                  <div className='relative'>
                    <div
                      className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${
                        isDemo ? 'blur-sm select-none pointer-events-none' : ''
                      }`}
                    >
                      <CostDispertionChart
                        data={processDataForChart(costsData, 'freight')}
                        title={getTitleForCostType('freight')}
                        unit={
                          filters.unitPrice.startsWith('box-')
                            ? `USD/caja`
                            : 'USD/kg'
                        }
                        separation={
                          filters.separation as 'none' | 'by_receiver'
                        }
                        loading={loading}
                        visualization={visualization}
                        othersKg={costsData?.freight?.othersKg || 0}
                        ownKg={costsData?.freight?.ownKg || 0}
                      />
                      <CostDispertionChart
                        data={processDataForChart(costsData, 'other_costs')}
                        title={getTitleForCostType('other_costs')}
                        unit={
                          filters.unitPrice.startsWith('box-')
                            ? `USD/caja`
                            : 'USD/kg'
                        }
                        separation={
                          filters.separation as 'none' | 'by_receiver'
                        }
                        loading={loading}
                        visualization={visualization}
                        othersKg={costsData?.other_costs?.othersKg || 0}
                        ownKg={costsData?.other_costs?.ownKg || 0}
                      />
                    </div>
                    {isDemo && (
                      <div className='absolute inset-0 z-10 flex items-center justify-center'>
                        <Alert className='text-center shadow'>
                          {t(
                            'Estadísticas ocultas en modo demo. Contrata CherryMarket para ver los valores reales.'
                          )}
                        </Alert>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </PageLayout>
    </div>
  );
};

export default SettlementsComparativeCosts;
